// Editor del `form_schema` de un favorite_type. Permite definir una lista
// reordenable de fields con (key, label, type, required). El valor resultante
// se guarda en `favorite_types.details.form_schema`.

import { useId } from 'react';
import { X } from 'lucide-react';
import type { FormFieldType, FormSchema, FormSchemaField } from '@/lib/api';
import { fieldCls, checkboxCls } from '@/lib/form-styles';

const FIELD_TYPES: Array<{ value: FormFieldType; label: string }> = [
  { value: 'string', label: 'Texto corto' },
  { value: 'text', label: 'Texto largo' },
  { value: 'url', label: 'URL' },
  { value: 'number', label: 'Número' },
  { value: 'boolean', label: 'Booleano (sí/no)' },
  { value: 'date', label: 'Fecha' },
];

/** Slugifica un label para usar como `key` de un field. */
export function deriveKey(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

export interface FormSchemaEditorProps {
  value: FormSchema;
  onChange: (next: FormSchema) => void;
  disabled?: boolean;
}

export default function FormSchemaEditor({
  value,
  onChange,
  disabled = false,
}: FormSchemaEditorProps): JSX.Element {
  const baseId = useId();
  const fields = value.fields;

  function patch(idx: number, partial: Partial<FormSchemaField>): void {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...partial } : f));
    onChange({ fields: next });
  }

  function addField(): void {
    const next: FormSchemaField = {
      key: '',
      label: '',
      type: 'string',
      required: false,
    };
    onChange({ fields: [...fields, next] });
  }

  function removeField(idx: number): void {
    onChange({ fields: fields.filter((_, i) => i !== idx) });
  }

  function move(idx: number, delta: -1 | 1): void {
    const target = idx + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const a = next[idx]!;
    const b = next[target]!;
    next[idx] = b;
    next[target] = a;
    onChange({ fields: next });
  }

  // Validaciones de duplicados (key vacio o repetido). Render-only — el guardado
  // se bloquea desde la pagina padre via validateFormSchema().
  const keys = fields.map((f) => f.key);
  const dupKeys = new Set(keys.filter((k, i) => k !== '' && keys.indexOf(k) !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          Campos que se piden al crear un item de este tipo. Cada item guarda
          sus valores en <code>details.type_parameters</code>.
        </p>
        <button
          type="button"
          onClick={addField}
          disabled={disabled}
          className="min-h-[2.5rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-3 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted disabled:opacity-50"
        >
          + Agregar campo
        </button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-ink-muted italic">
          Sin campos. Los items de este tipo solo tendran nombre + descripcion.
        </p>
      ) : null}

      <ul className="space-y-2">
        {fields.map((f, i) => {
          const keyError = f.key === '' ? 'Requerido' : dupKeys.has(f.key) ? 'Duplicado' : null;
          const labelError = f.label === '' ? 'Requerido' : null;
          return (
            <li
              key={`${baseId}-${i}`}
              className="bg-surface dark:bg-surface-dark rounded-lg border border-surface-border dark:border-surface-dark-border p-3 space-y-2"
            >
              <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,1fr,auto,auto] gap-2 items-end">
                <label className="text-xs space-y-1">
                  <span className="font-medium">Etiqueta</span>
                  <input
                    type="text"
                    value={f.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      // Auto-derivar key si todavia esta vacio o si era auto-derivado del label anterior.
                      const previousAuto = f.key === '' || f.key === deriveKey(f.label);
                      patch(i, previousAuto ? { label, key: deriveKey(label) } : { label });
                    }}
                    disabled={disabled}
                    placeholder="Ej: Escudo"
                    maxLength={60}
                    className={fieldCls()}
                  />
                  {labelError ? <span className="text-status-error">{labelError}</span> : null}
                </label>
                <label className="text-xs space-y-1">
                  <span className="font-medium">Key</span>
                  <input
                    type="text"
                    value={f.key}
                    onChange={(e) => patch(i, { key: deriveKey(e.target.value) })}
                    disabled={disabled}
                    placeholder="logo_url"
                    maxLength={40}
                    className={fieldCls({ mono: true })}
                  />
                  {keyError ? <span className="text-status-error">{keyError}</span> : null}
                </label>
                <label className="text-xs space-y-1">
                  <span className="font-medium">Tipo</span>
                  <select
                    value={f.type}
                    onChange={(e) => patch(i, { type: e.target.value as FormFieldType })}
                    disabled={disabled}
                    className={fieldCls()}
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs flex items-center gap-2 self-center mt-4 md:mt-0">
                  <input
                    type="checkbox"
                    checked={f.required}
                    onChange={(e) => patch(i, { required: e.target.checked })}
                    disabled={disabled}
                    className={checkboxCls()}
                  />
                  <span className="font-medium">Obligatorio</span>
                </label>
                <div className="flex gap-1 self-center mt-4 md:mt-0">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={disabled || i === 0}
                    aria-label="Subir campo"
                    title="Subir"
                    className="min-h-[2.5rem] min-w-[2.5rem] rounded border border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={disabled || i === fields.length - 1}
                    aria-label="Bajar campo"
                    title="Bajar"
                    className="min-h-[2.5rem] min-w-[2.5rem] rounded border border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    disabled={disabled}
                    aria-label="Eliminar campo"
                    title="Eliminar"
                    className="min-h-[2.5rem] min-w-[2.5rem] inline-flex items-center justify-center rounded border border-status-error/30 text-status-error hover:bg-status-error/10 disabled:opacity-30"
                  >
                    <X size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Valida un schema antes de guardarlo. Devuelve un mensaje de error o null.
 * Reglas: cada field debe tener key + label no vacios, keys unicos.
 */
export function validateFormSchema(schema: FormSchema): string | null {
  const keys = new Set<string>();
  for (const f of schema.fields) {
    if (!f.label.trim()) return 'Todos los campos necesitan una etiqueta.';
    if (!f.key.trim()) return 'Todos los campos necesitan una key.';
    if (!/^[a-z0-9_-]+$/.test(f.key)) {
      return `Key invalida "${f.key}". Solo a-z, 0-9, _ o -.`;
    }
    if (keys.has(f.key)) return `Key duplicada: "${f.key}".`;
    keys.add(f.key);
  }
  return null;
}

/**
 * Extrae el `form_schema` del jsonb `details` de un favorite_type. Tolera
 * shapes invalidas (devuelve schema vacio).
 */
export function readFormSchema(details: Record<string, unknown> | null | undefined): FormSchema {
  if (!details || typeof details !== 'object') return { fields: [] };
  const raw = (details as { form_schema?: unknown }).form_schema;
  if (!raw || typeof raw !== 'object') return { fields: [] };
  const rawFields = (raw as { fields?: unknown }).fields;
  if (!Array.isArray(rawFields)) return { fields: [] };
  const fields: FormSchemaField[] = [];
  for (const f of rawFields) {
    if (!f || typeof f !== 'object') continue;
    const obj = f as Partial<FormSchemaField>;
    if (typeof obj.key !== 'string' || typeof obj.label !== 'string') continue;
    if (typeof obj.type !== 'string') continue;
    if (!['string', 'text', 'url', 'number', 'boolean', 'date'].includes(obj.type)) continue;
    fields.push({
      key: obj.key,
      label: obj.label,
      type: obj.type as FormFieldType,
      required: obj.required === true,
    });
  }
  return { fields };
}
